import { useMutation } from '@tanstack/react-query';
import { UseFormReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { api } from 'src/common/utils/api';
import { Logger } from 'src/utils/Logger';
import {
  ProviderReferenceHolder,
  describeProviderReferenceHolders,
} from 'src/financial/financial-data/providerReferenceHolders';

type ValidationResponse = {
  is_duplicate: boolean;
  has_ambiguous_feed_matches?: boolean;
  other_holders?: ProviderReferenceHolder[];
};

type ValidatePayload = {
  providerReferenceNumber?: string | null;
  policyNumber?: string | null;
  assetId?: string;
  product?: string;
};

export const DUPLICATE_PROVIDER_REFERENCE_MESSAGE =
  'This provider reference number is already in use';
export const DUPLICATE_POLICY_NUMBER_MESSAGE =
  'This policy number is already in use for this provider reference';

export const useValidateProviderReference = (clientId: string) => {
  const {
    mutateAsync,
    isPending: isLoading,
    isError,
    error,
  } = useMutation<{ data: ValidationResponse }, Error, ValidatePayload>({
    mutationFn: ({ providerReferenceNumber, policyNumber, assetId, product }) =>
      api.post<ValidationResponse>(`clients/${clientId}/assets/validate-provider-reference`, {
        ...(providerReferenceNumber && { provider_reference_number: providerReferenceNumber }),
        ...(policyNumber && { policy_number: policyNumber }),
        ...(assetId && { asset_id: assetId }),
        ...(product && { product }),
      }),
  });

  const validateProviderReference = async (payload: ValidatePayload) => mutateAsync(payload);

  return {
    validateProviderReference,
    isLoading,
    isError,
    error,
  };
};

// With owners on, a joint holding is one item with every owner on it, not a copy each.
export const notifyIfProviderReferenceShared = (
  otherHolders: ProviderReferenceHolder[] = [],
  ownersEnabled = false,
) => {
  if (otherHolders.length === 0) {
    return;
  }
  const jointResolution = ownersEnabled
    ? 'record it once, with them as an owner'
    : "mark each client's copy as joint";
  notifications.show({
    color: 'yellow',
    title: 'Provider reference already in use',
    message: `This provider reference number is also used on an asset for ${describeProviderReferenceHolders(otherHolders)}. If this is a joint holding, ${jointResolution}.`,
    autoClose: false,
  });
};

export const validateProviderReferenceAndSetError = async (
  productData: Record<string, unknown>,
  clientId: string,
  form: UseFormReturnType<FormValuesType>,
  productConstant: string,
  validateProviderReference: (payload: ValidatePayload) => Promise<{ data: ValidationResponse }>,
  ownersEnabled = false,
): Promise<boolean> => {
  const providerReferenceNumber = productData?.provider_reference_number as string | undefined;
  const policyNumber = productData?.policy_number as string | undefined;

  if (!providerReferenceNumber && !policyNumber) {
    return true;
  }
  if (!clientId) {
    return true;
  }

  try {
    const result = await validateProviderReference({
      providerReferenceNumber,
      policyNumber,
      assetId: productData['id'] as string | undefined,
      product: productConstant,
    });
    if (result.data.is_duplicate) {
      const field = policyNumber ? 'policy_number' : 'provider_reference_number';
      const message = policyNumber
        ? DUPLICATE_POLICY_NUMBER_MESSAGE
        : DUPLICATE_PROVIDER_REFERENCE_MESSAGE;
      form.setFieldError(`${productConstant}.${field}`, message);
      return false;
    }
    if (result.data.has_ambiguous_feed_matches) {
      form.setFieldError(
        `${productConstant}.provider_reference_number`,
        'Multiple wrappers match this provider reference. Add a policy number to disambiguate.',
      );
      return false;
    }
    notifyIfProviderReferenceShared(result.data.other_holders, ownersEnabled);
  } catch (error) {
    Logger.warn('Provider reference validation failed', { error });
  }
  return true;
};
